import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  ExternalLink,
  Loader2,
  Search,
  X,
} from "lucide-react";
import {
  PARTNER_STATUSES,
  PARTNER_STATUS_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  type PartnerStatus,
  type PartnerType,
  type Tier,
} from "@colab/shared";
import { cn, formatRelative } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { PartnerTypeBadge } from "@/components/shared/partner-type-badge";
import { PartnerStatusBadge } from "@/components/shared/partner-status-badge";
import { TierBadge } from "@/components/shared/tier-badge";
import {
  useAdminPartnersList,
  type AdminPartnerListRow,
} from "./use-admin-partners";
import { useDefaultProgramTierMap } from "./use-default-program-tier-map";
import { useBulkClearTiers } from "./use-bulk-clear-tiers";
import { CreatePartnerSheet } from "./create-partner-sheet";

// Sentinel filter value for "no tier assignment" (tier_id IS NULL). Tier
// display + filtering are assignment-driven since 0085.
const TIER_FILTER_NONE = "__none__";

// Phase 7 sub-bucket 10 — admin partners list. Saved Views (filter trees,
// column catalog, view tabs) is deferred to its own sub-bucket — that
// surface spans multiple list pages (partners + deals + submissions etc.)
// and warrants a dedicated port. This page ships the search + chip filters
// (status / partner_type / tier) and sortable columns that the legacy
// PartnersToolbar exposed inline above the saved-view tabs.
//
// URL search params are the source of truth for filter/sort state so that
// browser back/forward and shareable URLs both work. The list query itself
// is unfiltered server-side (single GET caches across filter changes); all
// filtering and sorting happens in-memory against the cached rows. Fine at
// the current 500-row cap; pagination + server filters land with the
// Saved Views sub-bucket.
type SortDir = "asc" | "desc";
type SortKey =
  | "company_name"
  | "partner_type"
  | "tier"
  | "status"
  | "region"
  | "deal_count"
  | "intro_count"
  | "updated_at"
  | "created_at";

const SORTABLE: ReadonlySet<SortKey> = new Set([
  "company_name",
  "partner_type",
  "tier",
  "status",
  "region",
  "deal_count",
  "intro_count",
  "updated_at",
  "created_at",
]);

export function PartnersListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useAdminPartnersList();
  const { tierById, orderedTiers } = useDefaultProgramTierMap();
  const bulkClear = useBulkClearTiers();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const q = searchParams.get("q") ?? "";
  const statusFilter = searchParams.get("status") as PartnerStatus | null;
  const typeFilter = searchParams.get("partner_type") as PartnerType | null;
  // Tier filter is assignment-driven: a tier id, the "no assignment" sentinel,
  // or null (no filter).
  const tierFilter = searchParams.get("tier");
  const sortParam = (searchParams.get("sort") ?? "updated_at") as SortKey;
  const sortKey: SortKey = SORTABLE.has(sortParam) ? sortParam : "updated_at";
  const dirParam = (searchParams.get("dir") ?? "desc") as SortDir;
  const dir: SortDir = dirParam === "asc" ? "asc" : "desc";

  const filtersActive =
    Boolean(q.trim()) ||
    Boolean(statusFilter) ||
    Boolean(typeFilter) ||
    Boolean(tierFilter);

  const filtered = useMemo(() => {
    const rows = query.data ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (needle && !p.company_name.toLowerCase().includes(needle)) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (typeFilter && p.partner_type !== typeFilter) return false;
      if (tierFilter === TIER_FILTER_NONE && p.tier_id !== null) return false;
      if (tierFilter && tierFilter !== TIER_FILTER_NONE && p.tier_id !== tierFilter)
        return false;
      return true;
    });
  }, [query.data, q, statusFilter, typeFilter, tierFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const factor = dir === "asc" ? 1 : -1;
    // Tier sorts by the default program's tier position (low → high), with
    // unassigned partners last regardless of direction would be surprising —
    // instead unassigned sort to the extreme end of the chosen direction.
    if (sortKey === "tier") {
      const rank = (p: AdminPartnerListRow) => {
        const t = p.tier_id ? tierById.get(p.tier_id) : undefined;
        return t ? t.position : Number.MAX_SAFE_INTEGER;
      };
      arr.sort((a, b) => (rank(a) - rank(b)) * factor);
      return arr;
    }
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * factor;
      }
      const as = String(av ?? "");
      const bs = String(bv ?? "");
      return as.localeCompare(bs) * factor;
    });
    return arr;
  }, [filtered, sortKey, dir, tierById]);

  // Tier filter pills: the default program's tiers (filtering on tier_id) plus
  // a "None" option for unassigned partners. Empty when no default program.
  const tierFilterOptions = useMemo<
    ReadonlyArray<{ value: string; label: string }>
  >(() => {
    if (orderedTiers.length === 0) return [];
    return [
      ...orderedTiers.map((t) => ({ value: t.id, label: t.name })),
      { value: TIER_FILTER_NONE, label: "None" },
    ];
  }, [orderedTiers]);

  // Selection (for the bulk "Clear tier" action). Persists across filter
  // changes; the bulk action operates on whatever is selected.
  const visibleIds = useMemo(() => sorted.map((p) => p.id), [sorted]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected = visibleIds.some((id) => selected.has(id));

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }
  function clearSelection() {
    setSelected(new Set());
  }
  function handleBulkClear() {
    const ids = [...selected];
    if (ids.length === 0) return;
    bulkClear.mutate(ids, {
      onSuccess: (res) => {
        toast.success(
          `Cleared tier for ${res.cleared} partner${res.cleared === 1 ? "" : "s"}`,
        );
        clearSelection();
      },
      onError: (err) => toast.error(err.message ?? "Failed to clear tiers"),
    });
  }

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  function toggleSort(key: SortKey) {
    if (!SORTABLE.has(key)) return;
    const next = new URLSearchParams(searchParams);
    if (sortKey !== key) {
      next.set("sort", key);
      next.set("dir", "asc");
    } else {
      next.set("dir", dir === "asc" ? "desc" : "asc");
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Partners
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            All integration partners, referrers, resellers, agencies, and
            consultants.
          </p>
        </div>
        <CreatePartnerSheet />
      </header>

      <Toolbar
        q={q}
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        tierFilter={tierFilter}
        tierOptions={tierFilterOptions}
        onChange={setParam}
      />

      <div className="mt-6">
        {query.isLoading ? (
          <TableSkeleton />
        ) : query.isError ? (
          <EmptyState
            icon={Building2}
            title="Couldn't load partners"
            description={query.error?.message ?? "Try refreshing."}
          />
        ) : sorted.length > 0 ? (
          <PartnersTable
            rows={sorted}
            sortKey={sortKey}
            dir={dir}
            onSort={toggleSort}
            tierById={tierById}
            selected={selected}
            onToggleOne={toggleOne}
            onToggleAll={toggleAllVisible}
            allVisibleSelected={allVisibleSelected}
            someVisibleSelected={someVisibleSelected}
          />
        ) : filtersActive ? (
          <EmptyState
            icon={Search}
            title="No partners match these filters"
            description="Try clearing the search box or relaxing the filters."
          />
        ) : (
          <EmptyState
            icon={Building2}
            title="No partners yet"
            description="Add your first integration partner, reseller, agency, or consultant to start tracking deal flow."
            action={<CreatePartnerSheet variant="empty" />}
          />
        )}
      </div>

      {selected.size > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border bg-card/95 py-2 pl-4 pr-2 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
            <span className="text-sm font-medium tabular-nums">
              {selected.size} selected
            </span>
            <span className="h-4 w-px bg-border" />
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkClear}
              disabled={bulkClear.isPending}
              className="rounded-full"
            >
              {bulkClear.isPending && (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              )}
              Clear tier
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              disabled={bulkClear.isPending}
              className="rounded-full"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Toolbar({
  q,
  statusFilter,
  typeFilter,
  tierFilter,
  tierOptions,
  onChange,
}: {
  q: string;
  statusFilter: PartnerStatus | null;
  typeFilter: PartnerType | null;
  tierFilter: string | null;
  tierOptions: ReadonlyArray<{ value: string; label: string }>;
  onChange: (key: string, value: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          placeholder="Search by company name"
          onChange={(e) => onChange("q", e.target.value)}
          className="pl-8"
        />
      </div>
      <FilterGroup<PartnerStatus>
        label="Status"
        value={statusFilter}
        options={PARTNER_STATUSES.map((v) => ({
          value: v,
          label: PARTNER_STATUS_LABEL[v],
        }))}
        onChange={(next) => onChange("status", next)}
      />
      <FilterGroup<PartnerType>
        label="Type"
        value={typeFilter}
        options={PARTNER_TYPES.map((v) => ({
          value: v,
          label: PARTNER_TYPE_LABEL[v],
        }))}
        onChange={(next) => onChange("partner_type", next)}
      />
      {tierOptions.length > 0 && (
        <FilterGroup<string>
          label="Tier"
          value={tierFilter}
          options={tierOptions}
          onChange={(next) => onChange("tier", next)}
        />
      )}
    </div>
  );
}

function FilterGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T | null) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-border/70 bg-card/40 px-1.5 py-1">
      <span className="px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(value === o.value ? null : o.value)}
          className={cn(
            "rounded px-2 py-0.5 text-[11px] font-medium transition-colors",
            value === o.value
              ? "bg-accent/15 text-accent"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Phase 7 sub-bucket 19 — column-key-driven render map keyed off the
// `VIEW_COLUMN_CATALOG.partner` shape. `visibleColumns?: string[]` defaults
// to the legacy full set + trailing row-actions; future Saved Views surface
// for partners can pass `activeView.columns` through unchanged. Catalog keys
// match `segments-meta.ts::VIEW_COLUMN_CATALOG.partner` (with `region` +
// `updated_at` added on the legacy default since they're useful at-a-glance
// even when no view is active).
type ColumnDef = {
  key: string;
  align?: "left" | "right";
  sortable?: boolean;
  /** Sort key when the column key isn't itself sortable (e.g. partner_name
   *  for sub-bucket 18 catalog parity; this surface uses `company_name`). */
  sortBy?: SortKey;
  render: (p: AdminPartnerListRow) => React.ReactNode;
};

const PARTNERS_COLUMNS: Record<string, ColumnDef> = {
  company_name: {
    key: "company_name",
    sortable: true,
    render: (p) => (
      <Link
        to={`/admin/partners/${p.id}`}
        className="font-medium text-foreground hover:text-accent"
      >
        {p.company_name}
      </Link>
    ),
  },
  partner_type: {
    key: "partner_type",
    sortable: true,
    render: (p) => <PartnerTypeBadge type={p.partner_type} />,
  },
  tier: {
    key: "tier",
    sortable: true,
    // Overridden in PartnersTable with the assignment-driven cell; this static
    // fallback only renders if the catalog is used without the injected map.
    render: (p) =>
      p.tier ? (
        <TierBadge tier={p.tier} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  status: {
    key: "status",
    sortable: true,
    render: (p) => <PartnerStatusBadge status={p.status} />,
  },
  region: {
    key: "region",
    sortable: true,
    render: (p) => (
      <span className="text-muted-foreground">{p.region ?? "—"}</span>
    ),
  },
  deal_count: {
    key: "deal_count",
    sortable: true,
    align: "right",
    render: (p) => (
      <span className="tabular-nums text-muted-foreground">
        {p.deal_count}
      </span>
    ),
  },
  intro_count: {
    key: "intro_count",
    sortable: true,
    align: "right",
    render: (p) => (
      <span className="tabular-nums text-muted-foreground">
        {p.intro_count}
      </span>
    ),
  },
  primary_contact_email: {
    key: "primary_contact_email",
    render: (p) => (
      <span className="text-xs text-muted-foreground">
        {p.primary_contact_email ?? "—"}
      </span>
    ),
  },
  updated_at: {
    key: "updated_at",
    sortable: true,
    align: "right",
    render: (p) => (
      <span className="text-xs text-muted-foreground">
        {formatRelative(p.updated_at)}
      </span>
    ),
  },
  created_at: {
    key: "created_at",
    sortable: true,
    align: "right",
    render: (p) => (
      <span className="text-xs text-muted-foreground">
        {formatRelative(p.created_at)}
      </span>
    ),
  },
};

const PARTNER_COLUMN_LABEL: Record<string, string> = {
  company_name: "Company",
  partner_type: "Type",
  tier: "Tier",
  status: "Status",
  region: "Region",
  deal_count: "Deals",
  intro_count: "Intros",
  primary_contact_email: "Primary contact",
  updated_at: "Updated",
  created_at: "Created",
};

const PARTNERS_DEFAULT_VISIBLE: readonly string[] = [
  "company_name",
  "partner_type",
  "tier",
  "status",
  "region",
  "deal_count",
  "intro_count",
  "updated_at",
];

function PartnersTable({
  rows,
  sortKey,
  dir,
  onSort,
  tierById,
  selected,
  onToggleOne,
  onToggleAll,
  allVisibleSelected,
  someVisibleSelected,
  visibleColumns,
}: {
  rows: AdminPartnerListRow[];
  sortKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  tierById: Map<string, Tier>;
  selected: ReadonlySet<string>;
  onToggleOne: (id: string) => void;
  onToggleAll: () => void;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  /** Reserved for future Saved Views surface on partners — passing
   *  `activeView?.columns` will thread through unchanged. Today no consumer
   *  passes this, so the legacy column set renders. */
  visibleColumns?: string[];
}) {
  // Inject the assignment-driven tier cell into the static column catalog. The
  // catalog stays module-level (Saved Views reuses it); only the tier cell
  // needs the runtime map.
  const columns = useMemo<Record<string, ColumnDef>>(
    () => ({
      ...PARTNERS_COLUMNS,
      tier: {
        ...PARTNERS_COLUMNS.tier,
        render: (p) => <PartnerTierCell partner={p} tierById={tierById} />,
      },
    }),
    [tierById],
  );

  const cols = (visibleColumns && visibleColumns.length > 0
    ? visibleColumns
    : PARTNERS_DEFAULT_VISIBLE
  )
    .map((k) => columns[k])
    .filter((c): c is ColumnDef => Boolean(c));

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="w-10 px-4 py-2.5">
              <Checkbox
                aria-label="Select all on this page"
                checked={
                  allVisibleSelected
                    ? true
                    : someVisibleSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={onToggleAll}
              />
            </th>
            {cols.map((c) =>
              c.sortable ? (
                <SortableHeader
                  key={c.key}
                  name={(c.sortBy ?? c.key) as SortKey}
                  label={PARTNER_COLUMN_LABEL[c.key] ?? c.key}
                  sortKey={sortKey}
                  dir={dir}
                  onSort={onSort}
                  align={c.align ?? "left"}
                />
              ) : (
                <th
                  key={c.key}
                  className={`px-4 py-2.5 ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {PARTNER_COLUMN_LABEL[c.key] ?? c.key}
                </th>
              ),
            )}
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((p) => {
            const isSelected = selected.has(p.id);
            return (
              <tr
                key={p.id}
                data-state={isSelected ? "selected" : undefined}
                className="group transition-colors hover:bg-muted/30 data-[state=selected]:bg-accent/5"
              >
                <td className="px-4 py-3">
                  <Checkbox
                    aria-label={`Select ${p.company_name}`}
                    checked={isSelected}
                    onCheckedChange={() => onToggleOne(p.id)}
                  />
                </td>
                {cols.map((c) => (
                  <td
                    key={c.key}
                    className={`px-4 py-3 ${
                      c.align === "right" ? "text-right" : "text-left"
                    }`}
                  >
                    {c.render(p)}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/admin/partners/${p.id}`}
                    aria-label="Open partner"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:bg-background hover:text-foreground"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Tier display is assignment-driven: render the default-program tier the
// partner is assigned to (by tier_id), with badge + colour. No assignment
// (tier_id null) → empty em-dash.
function PartnerTierCell({
  partner,
  tierById,
}: {
  partner: AdminPartnerListRow;
  tierById: Map<string, Tier>;
}) {
  const tier = partner.tier_id ? tierById.get(partner.tier_id) : undefined;
  if (!tier) return <span className="text-muted-foreground">—</span>;
  return (
    <TierBadge
      assignment={{
        tierName: tier.name,
        badgePath: tier.badge_path,
        colorHex: tier.color_hex,
        legacyTier: tier.legacy_tier,
      }}
    />
  );
}

function SortableHeader({
  name,
  label,
  sortKey,
  dir,
  onSort,
  align = "left",
}: {
  name: SortKey;
  label: string;
  sortKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const isActive = sortKey === name;
  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-2.5 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(name)}
        className={cn(
          "inline-flex items-center gap-1 transition hover:text-foreground",
          isActive && "text-foreground",
        )}
      >
        {label}
        <Icon className="h-3 w-3 opacity-70" />
      </button>
    </th>
  );
}

function TableSkeleton() {
  return (
    <div className="rounded-xl border bg-card">
      <div className="space-y-3 p-4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="ml-auto h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
